import { GigsumoConstants } from 'src/app/services/GigsumoConstants';

/**
 * Sender / subject name for a notification — the primary line the
 * /notifications page shows in its left column (business name, person,
 * community, network, workspace, message group, or "GigSumo").
 *
 * The page's left column sometimes stacks extra lines (job title,
 * organisation). The slider only has room for the primary name, so that is
 * all this returns.
 */
const GIGSUMO = 'GigSumo';

const BUSINESS_NAME = new Set([
  'BUSINESS_EMPLOYEE_REQUEST_REJECTED',
  'BUSINESS_EMPLOYEE_REQUEST_ACCEPTED',
  'BUSINESS_PAGE_DEACTIVATED',
  'BUSINESS_PAGE_ACTIVATED',
  'JOB_SOURCE_TRANSFER',
  'JOB_TARGET_TRANSFER',
  'CANDIDATE_SOURCE_TRANSFER',
  'CANDIDATE_TARGET_TRANSFER',
  'PLAN_ACCESS_REVOKED_SOURCE_TRANSFER',
  'PLAN_ACCESS_REVOKED_JOB_TARGET_RECEIVE',
  'PLAN_ACCESS_REVOKED_CANDIDATE_TARGET_RECEIVE',
  'PLAN_ACCESS_REVOKED_WORKSPACE_TARGET_RECEIVE',
  'BUSINESS_EMPLOYEE_REVOKE',
  'BUSINESS_EMPLOYEE_ADDED',
  'BUSINESS_EMPLOYEE_INVITE_SENT',
  'BUSINESS_PAGE_FOLLOWER_INVITE_SENT',
  'BUSINESS_PAGE_CONTACT_INVITE',
  'BUSINESS_COLLABRATION_INVITE',
  GigsumoConstants.TEAM_DEACTIVATED,
  // contract notifications (generic block): business name, else GigSumo
  'CONTRACT_AGREEMENT_DOCUMENT_UPLOADED',
  'CONTRACT_COMPANY_DOC_CHECKLIST_CREATED',
  'CONTRACT_COMPANY_DOC_UPLOADED',
  'CONTRACT_COMPANY_DOC_VERIFIED',
  'CONTRACT_COMPANY_DOC_REJECTED',
  'CONTRACT_AGREEMENT_SIGNING_REQUESTED',
  'CONTRACT_AGREEMENT_SIGNED',
  'CONTRACT_AGREEMENT_ACCEPTED',
  'CONTRACT_AGREEMENT_REJECTED',
  'CONTRACT_WORK_ORDER_INITIATED',
  'CONTRACT_WORK_ORDER_ACCEPTED',
  'CONTRACT_WORK_ORDER_REJECTED',
  'CONTRACT_WORK_ORDER_REVISED',
  'CONTRACT_WORK_ORDER_VOIDED',
  'CONTRACT_INITIATED',
  'CONTRACT_INVOICE_SUBMITTED',
  'CONTRACT_INVOICE_UPDATED',
  'CONTRACT_INVOICE_APPROVED',
  'CONTRACT_INVOICE_REJECTED',
  'CONTRACT_INVOICE_PAID',
  'CONTRACT_PAYMENT_RECEIVED',
  'CONTRACT_INVOICE_VOIDED',
]);

const PERSON_NAME = new Set([
  'BUSINESS_FOLLOWER_ADDED',
  'POST_LIKED',
  'POST_COMMENTED',
  'POST_SHARED',
  'LEFT_FROM_MSG_GRP',
  'COMMUNITY_USER_INVITE_REQUEST_REJECTED',
  'COMMUNITY_USER_INVITE_REQUEST_ACCEPTED',
  'BUSINESS_FOLLOWER_INVITE_REQUEST_REJECTED',
  'BUSINESS_FOLLOWER_INVITE_REQUEST_ACCEPTED',
  'COMMUNITY_USER_JOIN_REQUEST',
  'CONNECT_REQUEST_RECEIVED',
  'CONNECTION_REMOVED',
  'JOINED_NETWORK',
  'User_itself_remove_from_Network',
  'Network_Removed',
  'NETWORK_INVITE_SENT',
  'NETWORK_CONNECT_REQUEST_REJECTED',
  'NETWORK_CONNECT_REQUEST_ACCEPTED',
  'JOINED_TEAM',
  GigsumoConstants.LEFT_TEAM,
  'TEAMS_REMOVED',
  'CONNECT_REQUEST_CANCELLED',
  'TEAM_CONNECT_REQUEST_ACCEPTED',
  'TEAM_CONNECT_REQUEST_CANCELLED',
  'BUSINESS_EMPLOYEE_INVITE_REQUEST',
  'CANDIDATE_INVITATION_REJECTED',
  'CANDIDATE_INVITATION_ACCEPTED',
  'CANDIDATE_ONBOARDED',
  'CANDIDATE_REJECTED',
  'CANDIDATE_PREONBOARDED',
  'JOB-OFFERED',
  'OFFER-WITHDRAWN',
  'CANDIDATE BACKOUT',
  'CANDIDATE-SHORTLISTED',
  'INTERVIEW-SCHEDULED',
  'CANDIDATE_GOT_SELECTED',
  'JOB_STATUS_CHANGED',
  'RESUME_REQUEST_ACCEPTED',
  'RESUME_UPDATED',
  'RESUME_REQUEST_INITIATED',
]);

const JOB_OWNER_NAME = new Set([
  'JOB_OWNER_REQUEST',
  'JOB_OWNER_ADDITIONAL_REQUEST',
  'CANDIDATE_DOCUMENT_REJECTION',
  'CANDIDATE_DOCUMENT_EXPIRED',
  'CANDIDATE_DOCUMENT_ALL_VERIFIED',
]);

const CANDIDATE_OWNER_NAME = new Set([
  'CANDIDATE_OWNER_SUBMISSION',
  'CANDIDATE_DOCUMENT_REUPLOAD',
  'CANDIDATE_EXPIRED_DOCUMENT_REUPLOAD',
]);

const COMMUNITY_NAME = new Set([
  'COMMUNITY_USER_INVITE_REQUEST',
  'COMMUNITY_PAGE_DEACTIVATED',
  'COMMUNITY_PAGE_ACTIVATED',
  'COMMUNITY_MEMBER_REQUEST_ACCEPTED',
  'COMMUNITY_MEMBER_REQUEST_REJECTED',
  'COMMUNITY_USER_REQUEST_REJECTED',
  'COMMUNITY_USER_REQUEST_ACCEPTED',
  'COMMUNITY_MEMBER_JOINED',
]);

const MESSAGE_GROUP_NAME = new Set(['REMOVED_FROM_MSG_GRP', 'ADDED_TO_MSG_GRP']);

const NETWORK_NAME = new Set(['NETWORK_DELETED', 'NETWORK_ACTIVATED', 'NETWORK_INVITE_CANCELLED']);

const TEAM_NAME = new Set([
  GigsumoConstants.TEAM_INACTIVE,
  GigsumoConstants.LEFT_TEAM_BY_ORG_CHANGE,
  GigsumoConstants.SUPERADMIN_INACTIVE,
  'TEAM_ACTIVATED',
  'TEAM_OWNER_UPDATE',
  'TEAM_INVITE_CANCELLED',
]);

function joinName(first: any, last: any): string {
  return [first, last].filter(v => v != null && String(v).trim()).join(' ').trim();
}

export function notificationSenderName(item: any): string {
  const type = String(item?.notifyType || '');
  const d = item?.notifyData || {};

  if (BUSINESS_NAME.has(type)) return d.businessName || GIGSUMO;
  if (PERSON_NAME.has(type)) return joinName(d.firstName, d.lastName) || GIGSUMO;
  if (JOB_OWNER_NAME.has(type)) return joinName(d.jobOwnerFirstName, d.jobOwnerLastName) || GIGSUMO;
  if (CANDIDATE_OWNER_NAME.has(type)) return joinName(d.CandidateOwnerFirstName, d.CandidateOwnerLastName) || GIGSUMO;
  if (COMMUNITY_NAME.has(type)) return d.communityName || GIGSUMO;
  if (MESSAGE_GROUP_NAME.has(type)) return d.messageGroupName || GIGSUMO;
  if (NETWORK_NAME.has(type)) return d.networkName || GIGSUMO;
  if (TEAM_NAME.has(type)) return d.teamName || GIGSUMO;
  // Everything else the page attributes to the platform itself.
  return GIGSUMO;
}
